<template>
  <div data-qa="cases-details">
    <div
      v-for="(caseDetails, index) in casesDeatils"
      :key="`case-${index}-${caseDetails.id}`"
      ref="casesDetailsRef"
      :class="{ 'mt-8': index !== 0 }"
      data-qa="cases-details_case-details"
      :data-popup-content="genDataPopupContent(caseDetails)"
    >
      <div v-if="index !== 0">
        <v-divider
          class="mb-4"
          data-qa="cases-details_case-details_divider"
        />
      </div>
      <div :class="xSpaceClass">
        <div class="d-flex align-center mb-3">
          <div
            data-qa="cases-details_case-details_title"
            class="text-subtitle-1 text--text mr-auto"
          >
            {{ $t('Случай', { ns: $i18next.language }) }} {{ index + 1 }}
          </div>
          <img
            data-qa="cases-details_case-details_logo"
            src="/static/_v1/pd/logos/ui-kit/prodoctorov-logo.svg"
            height="12"
            width="79"
            class="ma-2 ml-4 opacity-50"
          >
        </div>
        <div
          class="d-flex mb-4"
          :class="{ 'flex-column': isMobile }"
        >
          <div
            v-if="caseDetails.lpu"
            data-qa="cases-details_case-details_lpu"
            class="d-flex align-center"
            :class="isMobile ? 'mb-2' : 'mr-10 flex-basis-50'"
          >
            <span class="ui-icon-place ui-icon-fz-medium icon-secondary--text mr-2" />
            <a
              data-qa="cases-details_case-details_lpu_link"
              class="text-body-2 primary--text cursor-pointer link-decoration-none"
              :href="caseDetails.lpu.link"
            >{{ transDynamic(caseDetails.lpu, `name,${currentLanguageIso},${needTranslate}`) }}</a>
          </div>
          <div
            data-qa="cases-details_case-details_doctor"
            class="d-flex align-center"
            :class="{ 'flex-basis-50': !isMobile }"
          >
            <span class="ui-icon-doctor ui-icon-fz-medium icon-secondary--text mr-2" />
            <a
              data-qa="cases-details_case-details_doctor_link"
              class="text-body-2 primary--text cursor-pointer link-decoration-none"
              :href="doctor.link"
            >{{ transDynamic(doctor, `fio,${currentLanguageIso},${needTranslate}`) }}</a>
          </div>
        </div>
        <CaseImagesSlider
          v-if="isMobile"
          class="mb-6 mx-n4 px-4"
          :class="{ 'case-images-slider': caseDetails.images.length > 1 }"
          :case-images="caseDetails.images"
        />
        <CaseImages
          v-else
          class="mb-6"
          :case-images="caseDetails.images"
        />
        <div
          v-if="caseDetails.description"
          data-qa="cases-details_case-details_description"
          class="text-body-1 text--text text-break mb-4"
        >
          {{ transDynamic(caseDetails, `description,${currentLanguageIso},${needTranslate}`) }}
        </div>
        <div class="mb-4">
          <div
            class="text-body-2 text-secondary--text mb-1"
          >
            {{ $t('Стоимость выполнения аналогичных услуг', { ns: $i18next.language }) }}
          </div>
          <div
            data-qa="cases-details_case-details_total_price"
            class="text-subtitle-1 text--text mb-1"
          >
            {{ $t('от', { ns: $i18next.language }) }} {{ getFormattedPrice(caseDetails.total_price) }}
          </div>
          <div
            data-qa="cases-details_case-details_services_btn"
            class="text-body-2 primary--text cursor-pointer"
            @click="emit('click-services-details', caseDetails)"
          >
            {{ $t('Что входит в стоимость', { ns: $i18next.language }) }}
          </div>
        </div>
        <v-btn
          v-if="checkAppointmentBtn(caseDetails.lpu)"
          data-qa="cases-details_case-details_appointment_btn"
          color="primary"
          :block="isMobile"
          depressed
          @click="emitClickAppointment"
        >
          <span class="ui-icon-calendar-date-and-clock mr-2" />
          {{ $t('Записаться на консультацию', { ns: $i18next.language }) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, ref, watch, onMounted,
} from 'vue'
import transDynamic from 'djanjucks/filters/transDynamic'
import { getFormattedPrice, getFirstLetterUpper } from 'www/utils'
import getUserLanguage from 'src/utils/getUserLanguage'
import PopupContent from 'www/modules/PopupContent'
import SwiperModule from 'www/modules/SwiperModule'
import type { Doctor } from 'www/vue_components/common/DoctorCases/types'
import type { ApiDoctorCasesResponseGet } from 'www/vue_components/common/DoctorCases/api/apiDoctorCases.types'
import CaseImages from 'www/vue_components/common/DoctorCases/components/CaseImages.vue'
import CaseImagesSlider from 'www/vue_components/common/DoctorCases/components/CaseImagesSlider.vue'
import {
  useDoctorAppointmentCardStore,
} from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/stores'
import getPeriodText from 'www/vue_components/common/DoctorCases/functions/getPeriodText'

type DoctorAppointmentCardStore = ReturnType<ReturnType<typeof useDoctorAppointmentCardStore>>

const props = defineProps<{
  hasAppointmentSection: boolean
  doctor: Doctor
  casesDeatils: ApiDoctorCasesResponseGet['results']
  xSpaceClass: string
}>()

type Emits = {
  (_e: 'click-appointment'): void
  (_e: 'click-services-details', _caseId: typeof props['casesDeatils'][number]): void
}

const emit = defineEmits<Emits>()
const needTranslate = true
const currentLanguageIso = getUserLanguage()
const isMobile = window.MOBILE_VERSION
const casesDetailsRef = ref()

const lpuStores = computed(() => {
  if (!props.hasAppointmentSection) {
    return new Map<number, DoctorAppointmentCardStore>()
  }

  const lpuIds = new Set<number>()

  props.casesDeatils.forEach(({ lpu }) => {
    if (lpu) {
      lpuIds.add(lpu.id)
    }
  })

  const lpuStores = new Map<number, DoctorAppointmentCardStore>()

  lpuIds.forEach(lpuId => {
    lpuStores.set(lpuId, useDoctorAppointmentCardStore(lpuId)())
  })

  return lpuStores
})

function checkAppointmentBtn(lpu?: typeof props['casesDeatils'][number]['lpu']) {
  if (props.hasAppointmentSection && lpu && lpuStores.value.has(lpu.id)) {
    const { lpuAddress, hasSlots } = lpuStores.value.get(lpu.id) as DoctorAppointmentCardStore

    if (!lpuAddress || !lpuAddress.appointment_on) {
      return false
    }

    return (hasSlots && lpuAddress.appointment_direct) || lpuAddress.appointment_express
  }
}

onMounted(() => {
  if (isMobile) {
    // Достаточно один раз вызвать для всех слайдеров
    watch(() => props.casesDeatils, () => {
      new SwiperModule({
        module: {
          modifier: 'case-images-slider',
        },
        swiper: {
          navigation: false,
          pagination: false,
          slidesPerView: 'auto',
          spaceBetween: 0,
          centeredSlides: false,
          roundLengths: false,
        },
      })
    }, { deep: true, immediate: true })
  }

  new PopupContent({
    id: 'cases-gallery-modal',
    modifier: 'case-images',
    selector: '.case-image__image',
    ellipsis: true,
    truncateTitle: true,
    navigation: !isMobile,
    createHead: isMobile,
    pagination: isMobile ? 'dynamic' : 'fraction',
    createDescription: !isMobile,
    createHideButton: !isMobile,
    createHideCross: isMobile,
    modalOptions: {
      zIndex: 1001,
      useHash: false,
      animation: 'opacity',
    },
    ...(!isMobile && {
      sliderOptions: {
        width: 536,
        autoHeight: false,
      },
    }),
    markupDataLookup: true,
  } as any)
})

const genDataPopupContent = (caseDetails: ApiDoctorCasesResponseGet['results'][number]) => (
  JSON.stringify({
    data: caseDetails.images.map(img => {
      const periodText = getFirstLetterUpper(getPeriodText(img))

      return {
        name: periodText,
        src: img.photo,
        attributes: [{ name: periodText }],
      }
    }),
  })
)

function emitClickAppointment() {
  emit('click-appointment')
}

function scrollToCase(caseId: number) {
  if (casesDetailsRef.value) {
    const indexToScroll = props.casesDeatils.findIndex(({ id }) => id === caseId)

    if (indexToScroll !== -1) {
      casesDetailsRef.value[indexToScroll].scrollIntoView()
    }
  }
}

defineExpose({
  scrollToCase,
})
</script>

<style lang="scss" scoped>
.flex-basis-50 {
  flex-basis: 50%;
}

.opacity-50 {
  opacity: 0.5;
}
</style>
